import { AlertTriangle } from 'lucide-react';

import type { RouteCandidate } from '@/features/network/models';
import { effectiveLatency } from '@/features/routing/solver';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { formatCapacity, formatLatency, formatMoney } from '@/lib/format';
import { RoutePills } from '@/components/RoutePills';

function InlineMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3 dark:border-slate-800 dark:bg-slate-900/40">
      <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  );
}

export function RouteCard({ title, route, primary }: { title: string; route: RouteCandidate | null; primary?: boolean }) {
  if (!route) {
    return (
      <Card className="border-dashed p-6">
        <div className="text-base font-semibold">{title}</div>
        <div className="mt-2 text-sm text-slate-500 dark:text-slate-400">Подходящий маршрут не найден.</div>
      </Card>
    );
  }

  return (
    <Card className={primary ? 'border-emerald-300/70 shadow-emerald-500/10' : undefined}>
      <div className="space-y-5 p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-base font-semibold">{title}</div>
            <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">{route.hops} hop(s) · {route.edges.length} line(s)</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {primary ? <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-300">Primary</Badge> : null}
            {route.totalLatencyMs == null ? <Badge>Latency unknown</Badge> : null}
            {route.warnings.length > 0 ? <Badge>Warnings: {route.warnings.length}</Badge> : null}
          </div>
        </div>

        <RoutePills nodes={route.nodes} />

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <InlineMetric label="Суммарная задержка" value={formatLatency(route.totalLatencyMs)} />
          <InlineMetric label="MRC" value={formatMoney(route.totalMrc)} />
          <InlineMetric label="Bottleneck" value={formatCapacity(route.bottleneckMbps)} />
          <InlineMetric label="Эффективная цена" value={formatMoney(route.effectiveMonthlyCost)} />
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-800 dark:bg-slate-900/40">
          <div className="mb-3 text-sm font-medium">Сегменты маршрута</div>
          <div className="space-y-3">
            {route.edges.map((edge, index) => (
              <div key={`${edge.id}-${index}`} className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <Badge>{edge.subject || edge.id}</Badge>
                  <Badge>{edge.status || 'Unknown'}</Badge>
                  {edge.vendorName ? <Badge>{edge.vendorName}</Badge> : null}
                  {edge.manualReview ? <Badge className="border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">manual review</Badge> : null}
                </div>
                <div className="text-sm text-slate-800 dark:text-slate-200">
                  <div>{route.nodes[index]}</div>
                  <div className="my-1 text-slate-400">↓</div>
                  <div>{route.nodes[index + 1]}</div>
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-4">
                  <InlineMetric label="Capacity" value={formatCapacity(edge.capacityMbps)} />
                  <InlineMetric label="Latency" value={formatLatency(effectiveLatency(edge))} />
                  <InlineMetric label="MRC" value={formatMoney(edge.mrc)} />
                  <InlineMetric label="NRC" value={formatMoney(edge.nrc)} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {route.warnings.length > 0 ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-950 dark:bg-amber-950/30 dark:text-amber-200">
            <div className="mb-2 flex items-center gap-2 font-medium">
              <AlertTriangle className="h-4 w-4" /> Предупреждения
            </div>
            <ul className="list-disc space-y-1 pl-5">
              {route.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
